import React from 'react';
import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = '15 November 2025';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      heading: '1. Who we are',
      body: `Receevit Ltd provides a United Kingdom based client workspace that enables firms to send invoices, collect payments through Stripe, share supporting documents and monitor approvals in real time. This privacy notice explains how we handle personal data collected through receevit.com and the Receevit® client portal. Internal operational tools are governed by separate staff policies.`
    },
    {
      heading: '2. How to contact us',
      body: `Receevit Ltd is the data controller for the purposes of UK GDPR and the Data Protection Act 2018. You can reach us at receevit@outlook.com or by post at our registered address shown on the Companies House record. We aim to respond to privacy queries within 5 working days.`
    },
    {
      heading: '3. Personal data we process',
      list: [
        {
          title: 'Account and profile data',
          text: 'Names, business contact details, job titles, plan tier, subscription status and delivery preferences provided when you create or manage an account.'
        },
        {
          title: 'Financial workspace data',
          text: 'Invoice metadata, recipient information, approval history, payment schedules, discounts, early-payment incentives and attachments uploaded by you or your team.'
        },
        {
          title: 'Payment connectivity data',
          text: 'Stripe customer identifiers, Stripe Connect account status, VAT numbers, banking references and audit logs necessary to route funds securely. Card details are handled only by Stripe.'
        },
        {
          title: 'Communications and support',
          text: 'In-portal chat, notification delivery records, support tickets, email correspondence and preference centre selections.'
        },
        {
          title: 'Device, security and cookie data',
          text: 'IP addresses, browser/device information, access logs, security event telemetry, cookie and pixel identifiers used to operate the service and diagnose issues.'
        }
      ]
    },
    {
      heading: '4. How we use personal data',
      body: `We handle data to deliver and improve the client portal, to meet legal obligations and to maintain a secure environment. We avoid extraneous processing and always align activity with an appropriate lawful basis.`,
      list: [
        {
          title: 'Contractual necessity',
          text: 'Provision of the portal, automated payments, downloadable invoices (PDF/CSV) and account communications requested by you.'
        },
        {
          title: 'Legitimate interests',
          text: 'Product analytics, troubleshooting, fraud prevention, platform resilience and opt-out operational updates. We balance these interests against your rights.'
        },
        {
          title: 'Legal obligations',
          text: 'Tax/VAT records, accounting rules, anti-fraud controls and responding to competent authorities where legally required.'
        },
        {
          title: 'Consent',
          text: 'Optional marketing emails and non-essential cookies. You may withdraw consent at any time using in-app controls or by contacting us.'
        },
        {
          title: 'Analytics & product insights',
          text: 'We use Google Analytics to understand how visitors use our public site so we can improve onboarding and documentation. Analytics cookies only load after you grant consent and are limited to aggregate usage information (pages viewed, referrers, device type).'
        }
      ]
    },
    {
      heading: '5. Sharing and international transfers',
      body: `We rely on carefully selected processors who support the receevit.com application: Stripe (payments and compliance), MailerSend (transactional email with tracking disabled), Amazon Web Services (hosting, storage, database), Google Analytics (privacy-first usage analytics) and professional advisers when required. AWS regions are located in the UK (eu-west-2) and we use Standard Contractual Clauses where data moves outside the UK.`
    },
    {
      heading: '6. Data retention',
      list: [
        {
          title: 'Invoices and payment records',
          text: 'Kept for 7 years to satisfy accounting and tax duties.'
        },
        {
          title: 'Security events and access logs',
          text: 'Held for up to 24 months to investigate incidents and demonstrate compliance.'
        },
        {
          title: 'Attachments',
          text: 'Retained whilst the related invoice remains available and up to 12 months afterwards unless you delete them sooner.'
        },
        {
          title: 'Backups and snapshots',
          text: 'Maintained according to our documented backup schedule and then securely destroyed.'
        }
      ]
    },
    {
      heading: '7. Your rights',
      body: `You may request access, correction, deletion, restriction, portability or object to certain processing. You can update most profile data directly inside the portal. To exercise other rights please email receevit@outlook.com and we will respond within one calendar month. You also have the right to complain to the Information Commissioner’s Office (ico.org.uk).`
    },
    {
      heading: '8. Security',
      body: `Receevit® uses encrypted transport (TLS 1.2+), encrypted storage within AWS, role-based access controls, detailed audit logging, automated backups and regional redundancy. Maintenance Mode may be invoked to safeguard data whilst critical updates complete.`
    },
    {
      heading: '9. Automated decision-making',
      body: `Receevit® does not make solely automated decisions that have legal or similarly significant effects. Approval workflows are triggered by rules you configure, and humans can override them at any point.`
    },
    {
      heading: '10. Changes to this notice',
      body: `We review this policy whenever we introduce new features or processors. Material changes will be highlighted within the portal or by email at least 14 days before they take effect.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#1F11ED] px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Privacy Policy | Receevit®</title>
        <meta
          name="description"
          content="Learn how Receevit Ltd processes and protects personal data collected through receevit.com and the Receevit® client portal."
        />
        <link rel="canonical" href="https://www.receevit.com/privacy" />
        <meta property="og:title" content="Privacy Policy | Receevit®" />
        <meta property="og:description" content="Learn how Receevit Ltd processes and protects personal data collected through receevit.com and the Receevit® client portal." />
        <meta property="og:url" content="https://www.receevit.com/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
      </Helmet>

      <div className="max-w-4xl mx-auto text-white py-24">
        <div className="mb-10">
          <p className="text-sm font-semibold text-white/80 uppercase tracking-wider">Privacy Policy</p>
          <h1 className="text-4xl font-bold text-white mt-2 mb-4">Protecting your data at Receevit®</h1>
          <p className="text-lg text-white/85">
            We design Receevit® so that organisations worldwide can collaborate on invoicing and payments with confidence. This policy describes
            the personal information we collect, how it is used and the controls available to you.
          </p>
          <div className="mt-6 p-4 bg-white/10 rounded-2xl text-sm text-white">
            <strong>Last updated:</strong> {LAST_UPDATED}
          </div>
        </div>

        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-2xl font-semibold text-white mb-4">{section.heading}</h2>
              {section.body && <p className="text-white/85 leading-relaxed">{section.body}</p>}
              {section.list && (
                <ul className="mt-4 space-y-4">
                  {section.list.map((item) => (
                    <li key={item.title} className="p-4 bg-white/5 rounded-2xl">
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-white/85 text-sm mt-1">{item.text}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 p-6 bg-white/10 text-white rounded-3xl">
          <h3 className="text-xl font-semibold mb-3">Need to speak with us?</h3>
          <p>
            Email <a href="mailto:receevit@outlook.com" className="underline font-semibold">receevit@outlook.com</a> with any questions or to exercise your data protection rights. Please include enough detail for us to identify your account securely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

